import { Bell, Search, Plus, ShieldCheck } from 'lucide-react';
import {
  LayoutDashboard, CreditCard, Sparkles, Receipt, Bot, Settings,
  Trophy, AlertTriangle,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ThemeToggle } from '@/components/shell/ThemeToggle';
import { MobileNavDrawer } from '@/components/shell/MobileNavDrawer';
import { useAuth } from '@/lib/auth';

const GOAL_LABEL = {
  travel: 'Travel',
  cashback: 'Cashback',
  milestone: 'Milestones',
  miles: 'Miles',
  overall: 'Overall',
};

function initialsFromName(name) {
  if (!name) return 'U';
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

// Single source of truth for the consumer nav (shared by sidebar + mobile drawer).
// Note: the admin entrypoint is added dynamically inside TopBar() based on user.is_admin.
const CONSUMER_NAV_GROUPS_BASE = [
  {
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
      { to: '/cards', label: 'My Cards', icon: CreditCard, testid: 'nav-cards' },
      { to: '/recommend', label: 'Recommend', icon: Sparkles, testid: 'nav-recommend', badge: 'Hero' },
      { to: '/transactions', label: 'Transactions', icon: Receipt, testid: 'nav-transactions' },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { to: '/leakage', label: 'Leakage Center', icon: AlertTriangle, testid: 'nav-leakage' },
      { to: '/milestones', label: 'Milestones', icon: Trophy, testid: 'nav-milestones' },
    ],
  },
  {
    label: 'You',
    items: [
      { to: '/assistant', label: 'AI Assistant', icon: Bot, testid: 'nav-assistant' },
      { to: '/settings', label: 'Settings', icon: Settings, testid: 'nav-settings' },
    ],
  },
];

export const TopBar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Show the "Switch to admin" entry inside the mobile drawer when the signed-in
  // user is an admin — gives a second discovery path in addition to the icon button.
  const navGroups = user?.is_admin
    ? [
        ...CONSUMER_NAV_GROUPS_BASE,
        {
          label: 'Operations',
          items: [
            { to: '/admin', label: 'Admin portal', icon: ShieldCheck, badge: 'Ops', testid: 'nav-admin' },
          ],
        },
      ]
    : CONSUMER_NAV_GROUPS_BASE;
  return (
    <header className="sticky top-0 z-30 bg-[hsl(var(--background))]/85 backdrop-blur border-b border-border">
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-6 lg:px-8 h-14 sm:h-16">
        {/* Mobile-only hamburger that opens the full consumer nav drawer */}
        <MobileNavDrawer
          groups={navGroups}
          title="CardAssist Online"
          subtitle="India · Beta"
          testid="consumer-mobile-nav"
        />

        {/* Search — full-width on lg+, icon-only opens it on mobile */}
        <div className="flex-1 min-w-0 max-w-xl">
          {/* Mobile: icon-only stub that focuses the input within a sheet-like drop OR navigates to a search route.
              For now we keep the inline search but hidden on the very narrowest viewport — replaced by an icon button. */}
          <div className="relative hidden sm:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-muted-foreground" />
            <Input
              data-testid="app-global-search"
              placeholder="Search merchants, cards, transactions..."
              className="pl-10 h-10 bg-[hsl(var(--surface-2))] border-transparent focus:border-primary/40"
            />
          </div>
          {/* On <sm screens we hide search to save space; users can still navigate via the drawer */}
        </div>

        {user?.is_admin && (
          <>
            {/* Desktop: full button */}
            <Button
              asChild
              variant="outline"
              size="sm"
              className="hidden md:inline-flex gap-1.5 h-10 border-accent/40 text-accent hover:bg-accent/10 hover:text-accent"
              data-testid="topbar-open-admin"
            >
              <Link to="/admin">
                <ShieldCheck className="h-3.5 w-3.5" />
                Admin
                <Badge variant="outline" className="text-[9px] uppercase tracking-wider h-4 px-1 ml-0.5 border-accent/30">Ops</Badge>
              </Link>
            </Button>
            {/* Mobile: compact icon-pill */}
            <Button
              asChild
              variant="outline"
              size="icon"
              className="md:hidden h-9 w-9 shrink-0 border-accent/40 text-accent hover:bg-accent/10 hover:text-accent"
              data-testid="topbar-open-admin-mobile"
              aria-label="Open admin portal"
            >
              <Link to="/admin">
                <ShieldCheck className="h-[18px] w-[18px]" />
              </Link>
            </Button>
          </>
        )}

        <Button
          variant="outline"
          onClick={() => navigate('/recommend')}
          className="hidden md:inline-flex gap-2 h-10"
          data-testid="topbar-recommend-button"
        >
          <Plus className="h-4 w-4" />
          New recommendation
        </Button>

        {/* Compact recommend button on mobile */}
        <Button
          variant="ghost"
          size="icon"
          aria-label="New recommendation"
          onClick={() => navigate('/recommend')}
          className="md:hidden h-9 w-9 shrink-0"
          data-testid="topbar-recommend-mobile"
        >
          <Sparkles className="h-[18px] w-[18px] text-accent" />
        </Button>

        <ThemeToggle />

        <Button
          variant="ghost"
          size="icon"
          className="rounded-full relative hidden sm:inline-flex"
          aria-label="Notifications"
        >
          <Bell className="h-[18px] w-[18px]" />
          <span className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-accent" />
        </Button>

        <Link to="/settings" className="flex items-center gap-2.5 pl-1 shrink-0" data-testid="topbar-profile-link">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name || 'profile'}
              className="h-9 w-9 rounded-full border border-primary/20 object-cover"
              referrerPolicy="no-referrer"
              data-testid="topbar-profile-avatar"
            />
          ) : (
            <div className="h-9 w-9 rounded-full bg-primary/10 border border-primary/20 grid place-items-center text-sm font-semibold text-primary" data-testid="topbar-profile-initials">
              {initialsFromName(user?.name)}
            </div>
          )}
          <div className="hidden md:block leading-tight">
            <div className="text-sm font-medium" data-testid="topbar-profile-name">{(user?.name || 'Guest').split(' ')[0]}</div>
            <div className="text-[11px] text-muted-foreground" data-testid="topbar-profile-goal">Goal · {GOAL_LABEL[user?.goal] || 'Travel'}</div>
          </div>
        </Link>
      </div>
    </header>
  );
};
